import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Sparkles, ShieldCheck, Lock } from 'lucide-react';
import { PricingCards } from './PricingCards';
import { UPGRADE_MODAL_COPY } from '../constants/upgradeModalCopy';
import { useBillingStore } from '@/stores/useBillingStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { usePostApiWorkspacesWorkspaceIdBillingCreateCheckoutSession } from '@/api/generated/billing/billing';
import { toast } from 'sonner';
import type { CreateCheckoutSessionResponse } from '@/types';

export interface UpgradeModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  title: controlledTitle,
  description: controlledDescription,
}) => {
  const [loadingTierId, setLoadingTierId] = useState<string | null>(null);
  const { activeWorkspaceId } = useWorkspaceStore();

  const isStoreOpen = useBillingStore((s) => s.isUpgradeModalOpen);
  const storeTitle = useBillingStore((s) => s.upgradeModalTitle);
  const storeDescription = useBillingStore((s) => s.upgradeModalDescription);
  const closeUpgradeModal = useBillingStore((s) => s.closeUpgradeModal);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : isStoreOpen;
  const handleOpenChange = isControlled
    ? (controlledOnOpenChange ?? (() => {}))
    : (open: boolean) => {
        if (!open) closeUpgradeModal();
      };

  const modalTitle =
    controlledTitle ??
    storeTitle ??
    UPGRADE_MODAL_COPY.default.title;

  const modalDescription =
    controlledDescription ??
    storeDescription ??
    UPGRADE_MODAL_COPY.default.description;

  const {
    plans,
    isLoadingPlans,
    plansError,
    fetchPlans,
    status,
  } = useBillingStore();
  const currentPlanId = status?.planId || (!status?.isSubscribed ? 'free' : 'pro');

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen, fetchPlans]);

  const { mutate: createCheckoutSession, isPending: isCreatingCheckout } =
    usePostApiWorkspacesWorkspaceIdBillingCreateCheckoutSession({
      mutation: {
        onSuccess: (data: any) => {
          const response = data as unknown as CreateCheckoutSessionResponse;
          if (response?.checkoutUrl) {
            window.location.href = response.checkoutUrl;
          } else {
            toast.error('Não foi possível inicializar o checkout. Por favor, tente novamente.');
            setLoadingTierId(null);
          }
        },
        onError: (error: any) => {
          setLoadingTierId(null);
          const errorMsg =
            error?.response?.data?.description ||
            error?.response?.data?.error ||
            'Falha ao iniciar a sessão de checkout. Por favor, tente novamente.';
          toast.error(errorMsg);
        },
      },
    });

  const handleSelectPlan = (tier: { id: string; name: string }, _isAnnual: boolean, priceId: string) => {
    if (!activeWorkspaceId) {
      toast.error('Selecione um workspace primeiro.');
      return;
    }
    setLoadingTierId(tier.id);
    createCheckoutSession({
      workspaceId: activeWorkspaceId,
      data: {
        priceId,
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-6 sm:p-8">
        <DialogHeader className="text-center sm:text-center space-y-2 pb-2">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <DialogTitle className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
            {modalTitle}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground max-w-xl mx-auto">
            {modalDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="pt-2 pb-4">
          <PricingCards
            plans={plans}
            isLoadingPlans={isLoadingPlans}
            plansError={plansError}
            onRetryPlans={fetchPlans}
            onSelectPlan={handleSelectPlan}
            loadingTierId={loadingTierId}
            isSubmitting={isCreatingCheckout}
            currentPlanId={currentPlanId}
          />
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 pt-4 border-t border-border/50 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            <span>Checkout 100% Seguro via Stripe</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-primary" />
            <span>Ativação Instantânea na sua Loja</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Lock className="h-4 w-4 text-blue-500" />
            <span>Cancele a Qualquer Momento Sem Multas</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
